/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(async () => {
        await this.syncVariantAvailabilityFromStorefront();
        this.variant = this.getSelectedVariant();
        if (this.variant) {
          this.updateVariantInput();
        }
        this.updateAvailability();
        this.updateAddToCartButton();
        this.logVariantAvailability('init');
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      const changedSelector = evt.target.closest('.option-selector');
      const changedIndex = changedSelector
        ? Array.from(this.optionSelectors).indexOf(changedSelector)
        : -1;

      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if ((!this.variant || !this.variant.available) && changedIndex >= 0) {
        const fallback = this.data.product.variants.find((v) => {
          if (!v.available) return false;
          for (let i = 0; i <= changedIndex; i += 1) {
            if (v.options[i] !== this.selectedOptions[i]) return false;
          }
          return true;
        });
        if (fallback) {
          this.setOptionsToVariant(fallback, changedIndex);
          this.selectedOptions = fallback.options;
          this.variant = fallback;
          this.preSelection = false;
        }
      }

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.logVariantAvailability('change');
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Sets option selectors to match a variant (for indices after changedIndex).
     * @param {object} variant - Variant whose options to apply.
     * @param {number} fromIndex - Only update selectors at this index and after.
     */
    setOptionsToVariant(variant, fromIndex) {
      this.optionSelectors.forEach((selector, index) => {
        if (index < fromIndex) return;
        const value = variant.options[index];
        if (selector.dataset.selectorType === 'dropdown') {
          const customSelect = selector.querySelector('custom-select');
          const options = customSelect?.querySelectorAll('.custom-select__option') || [];
          const optionEl = Array.from(options).find((opt) => opt.dataset.value === value);
          if (customSelect && optionEl && optionEl !== customSelect.selectedOption) {
            customSelect.selectOption(optionEl);
          }
        } else {
          const inputs = selector.querySelectorAll('input.js-option');
          const input = Array.from(inputs).find((inp) => inp.value === value);
          if (input && !input.checked) {
            input.checked = true;
          }
        }
      });
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      const isBuyable = exists && available;
      el.classList.toggle('is-unavailable', exists && !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = isBuyable;
        }

        if (!isBuyable) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!isBuyable) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Syncs variant.available from the live storefront API (avoids stale cached HTML product JSON).
     * @returns {Promise<boolean>} Whether any availability flag changed.
     */
    async syncVariantAvailabilityFromStorefront() {
      const handle = this.data?.product?.handle;
      if (!handle) return false;

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) return false;

        const liveProduct = await res.json();
        const liveAvailability = new Map(
          liveProduct.variants.map((v) => [v.id, v.available])
        );

        let changed = false;
        this.data.product.variants.forEach((variant) => {
          const liveAvailable = liveAvailability.get(variant.id);
          if (typeof liveAvailable === 'boolean' && liveAvailable !== variant.available) {
            variant.available = liveAvailable;
            changed = true;
          }
        });

        if (changed && VariantPicker.isDebugEnabled()) {
          const availableCount = this.data.product.variants.filter((v) => v.available).length;
          console.info(
            `[VariantPicker] Synced availability from /products/${handle}.js — ${availableCount} variant(s) available`
          );
        }

        return changed;
      } catch (err) {
        return false;
      }
    }

    /**
     * Reads per-variant inventory from product-inventory Liquid JSON (if block is on the page).
     * @returns {object}
     */
    getLiquidInventoryData() {
      const inventoryEl = this.section?.querySelector('product-inventory [type="application/json"]');
      if (!inventoryEl) return {};

      try {
        const rows = JSON.parse(inventoryEl.textContent);
        return rows.reduce((acc, row) => {
          acc[row.id] = row;
          return acc;
        }, {});
      } catch (err) {
        return {};
      }
    }

    /**
     * Debug: add ?variant_debug=1 to the URL or run localStorage.setItem('mack_variant_debug', '1')
     */
    static isDebugEnabled() {
      try {
        if (window.location.search.includes('variant_debug=1')) return true;
        if (localStorage.getItem('mack_variant_debug') === '1') return true;
      } catch (err) {
        return false;
      }
      return false;
    }

    /**
     * Logs variant availability to the console for comparison with Shopify admin.
     * @param {string} context - Where the log was triggered from.
     */
    logVariantAvailability(context) {
      if (!VariantPicker.isDebugEnabled()) return;

      const { product, formatted } = this.data;
      const liquidInventory = this.getLiquidInventoryData();
      const variants = product.variants.map((v) => {
        const liquid = liquidInventory[v.id] || {};
        return {
          id: v.id,
          title: v.title,
          options: v.options.join(' / '),
          available: v.available,
          inventory_quantity_json: v.inventory_quantity,
          inventory_quantity_liquid: liquid.inventory_quantity,
          inventory_management: v.inventory_management,
          inventory_policy_json: v.inventory_policy,
          inventory_policy_liquid: liquid.inventory_policy,
          available_liquid: liquid.available,
          sku: v.sku || '',
          theme_inventory_flag: formatted[v.id]?.inventory || null
        };
      });

      const optionStates = [];
      this.querySelectorAll('.js-option').forEach((el) => {
        const value = el.dataset.value ?? el.value;
        optionStates.push({
          value,
          checked: Boolean(el.checked),
          exists: !el.classList.contains('is-nonexistent'),
          buyable: !el.classList.contains('is-unavailable'),
          css_nonexistent: el.classList.contains('is-nonexistent'),
          css_unavailable: el.classList.contains('is-unavailable')
        });
      });

      const selected = this.variant
        ? {
            id: this.variant.id,
            title: this.variant.title,
            options: this.variant.options.join(' / '),
            available: this.variant.available,
            inventory_quantity: this.variant.inventory_quantity
          }
        : null;

      const availableCount = variants.filter((v) => v.available).length;
      const liquidAvailableCount = variants.filter((v) => v.available_liquid === true).length;

      console.groupCollapsed(`[VariantPicker] ${product.title} (${context})`);
      console.log('Product URL', this.dataset.url);
      console.log('Availability method', this.dataset.availabilityMethod);
      console.log('Show availability', this.dataset.showAvailability);
      console.log('Selected options', this.selectedOptions);
      console.log('Selected variant', selected);
      console.log('Variants (theme JSON on page load)');
      console.table(variants);
      if (availableCount === 0) {
        console.warn(
          '[VariantPicker] Shopify reports 0 available variants in product JSON. '
          + 'The UI cannot show buyable options — check inventory location, Online Store channel, and Frengee vs native Shopify stock.'
        );
      }
      if (Object.keys(liquidInventory).length > 0) {
        console.log(
          'inventory_quantity_liquid / available_liquid come from product-inventory snippet (server Liquid)'
        );
        if (liquidAvailableCount > 0 && availableCount === 0) {
          console.error(
            '[VariantPicker] Liquid inventory shows stock but product JSON says all unavailable'
          );
        }
      }
      console.log('Option UI state (after availability update)');
      console.table(optionStates);
      console.log(
        'Disable: localStorage.removeItem("mack_variant_debug") or remove ?variant_debug=1 from URL'
      );
      console.groupEnd();

      if (!this.debugLiveFetchDone && product.handle) {
        this.debugLiveFetchDone = true;
        fetch(`/products/${product.handle}.js`)
          .then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.json();
          })
          .then((liveProduct) => {
            const liveVariants = liveProduct.variants.map((v) => ({
              id: v.id,
              title: v.title,
              options: [v.option1, v.option2, v.option3].filter(Boolean).join(' / '),
              available: v.available,
              inventory_quantity: v.inventory_quantity,
              inventory_management: v.inventory_management,
              inventory_policy: v.inventory_policy,
              sku: v.sku || ''
            }));
            console.groupCollapsed(`[VariantPicker] Live /products/${product.handle}.js`);
            console.table(liveVariants);
            const themeUnavailable = variants.filter((v) => !v.available).map((v) => v.id);
            const liveUnavailable = liveVariants.filter((v) => !v.available).map((v) => v.id);
            const onlyInTheme = themeUnavailable.filter((id) => !liveUnavailable.includes(id));
            const onlyInLive = liveUnavailable.filter((id) => !themeUnavailable.includes(id));
            if (onlyInTheme.length || onlyInLive.length) {
              console.warn('Mismatch vs live API', { onlyInTheme, onlyInLive });
            } else {
              console.log('available flags match live API');
            }
            const liveAvailable = liveVariants.filter((v) => v.available);
            console.log(`${liveAvailable.length}/${liveVariants.length} variants available on storefront API`);
            console.groupEnd();
          })
          .catch((err) => {
            console.warn('[VariantPicker] Could not fetch live product JSON', err);
          });
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                const wasBuyable = !optionEl.classList.contains('is-unavailable');
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available || wasBuyable
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
